import type { PnlDimensionGroup, PnlDimensionHierarchy } from 'src/components/star-grid/pnlDimension.types';
import { apiRequest, getDefinition, rememberGet } from 'src/components/star-grid/services/starGridHttp';
import { ReportDefinitionApiError } from 'src/components/star-grid/utils/reportDefinitionError';
import { parsePnlDimensionHierarchies } from 'src/components/star-grid/utils/pnlDimensionSchema';
import { PNL_DIMENSIONS_PATH } from 'src/components/star-grid/reportStudioConstants';

/** A dimension's hierarchy list barely changes; the filter bar and the pickers ask for it on every report. */
const HIERARCHIES_MAX_AGE_MS = 5 * 60_000;
const hierarchiesPath = (dimension: string): string => `${PNL_DIMENSIONS_PATH}/${encodeURIComponent(dimension)}/hierarchies`;

export const listPnlDimensionHierarchies = async (dimension: string, signal?: AbortSignal): Promise<PnlDimensionHierarchy[]> => {
  try {
    return await parsePnlDimensionHierarchies(await getDefinition(hierarchiesPath(dimension), signal, HIERARCHIES_MAX_AGE_MS));
  } catch (error) {
    if (error instanceof ReportDefinitionApiError && error.status === 404) return [];
    throw error;
  }
};

const ALL_HIERARCHIES_PATH = '/pnl-dimension-hierarchies';

export const listSelectablePnlHierarchies = async (signal?: AbortSignal): Promise<PnlDimensionGroup[]> => {
  const raw = await apiRequest(ALL_HIERARCHIES_PATH, undefined, signal);
  if (!raw || typeof raw !== 'object') throw new Error('Malformed dimension hierarchy response');
  return Promise.all(
    Object.entries(raw).map(async ([dimension, value]) => {
      // One malformed dimension must not take every other dimension's bindings off the shelves.
      try {
        const hierarchies = await parsePnlDimensionHierarchies(value);
        rememberGet(hierarchiesPath(dimension), value);
        return { dimension, hierarchies };
      } catch (error) {
        console.error(`[StarGrid] ${dimension} hierarchies are malformed; it contributes no bindings`, error);
        return { dimension, hierarchies: [] };
      }
    })
  );
};
